import { ErrorHandler, Injectable, Injector, inject } from "@angular/core";
import { init, captureException } from "@sentry/angular";

import { AppConfigService } from "./app.config.service";
import { debugError } from "./utils";
import { AUVIOUS_PURECLOUD_VERSION } from "../core-ui.enums";
import { localStore } from "@auvious/utils";
import { Subject } from "rxjs";

const isSentryEnabled = (config: AppConfigService) =>
  config.isSentryEnabled &&
  location.hostname !== "localhost" &&
  !localStore.getItem("auvious.sentry.disabled");

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  private injector = inject(Injector);

  constructor() {
    const config = this.injector.get(AppConfigService);
    if (isSentryEnabled(config)) {
      init({
        dsn: config.sentryDSN,
        environment: config.sentryEnvironment,
        release: window[AUVIOUS_PURECLOUD_VERSION] || "NO-VERSION",
        beforeBreadcrumb(event, hint) {
          try {
            const target = hint?.event?.target;
            if (target?.tagName) {
              event.message = `${event.message} (${
                target.hasAttribute("data-tid")
                  ? target.getAttribute("data-tid")
                  : target.parentElement.hasAttribute("data-tid")
                  ? target.parentElement.getAttribute("data-tid")
                  : target.innerText.trim().slice(0, 100)
              })`;
            }
          } catch {}

          return event;
        },
      });
    }
  }

  handleError(error) {
    captureException(error?.originalError || error);
    console.error(error);
  }
}

@Injectable()
export class GenericErrorHandler implements ErrorHandler {
  private sentry = inject(SentryErrorHandler);
  private config = inject(AppConfigService);

  private readonly connectionErrorGracePeriodMs = 5000;
  private connectionErrorTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingConnectionError: unknown = null;

  private _connectionError = new Subject();
  public connectionError$ = this._connectionError.asObservable();

  public notifyConnectionError(error) {
    this.pendingConnectionError = error;

    if (this.connectionErrorTimeout) {
      return;
    }

    this.connectionErrorTimeout = setTimeout(() => {
      this.connectionErrorTimeout = null;
      this._connectionError.next(this.pendingConnectionError);
    }, this.connectionErrorGracePeriodMs);
  }

  public notifyConnectionRecovered() {
    this.pendingConnectionError = null;

    if (!this.connectionErrorTimeout) {
      return;
    }

    clearTimeout(this.connectionErrorTimeout);
    this.connectionErrorTimeout = null;
  }

  public handleNotAuthenticatedError(error) {
    if (
      error.message.includes("non authenticated client") ||
      error.code === "ERR_NETWORK"
    ) {
      this.notifyConnectionError(error);
    } else {
      throw error;
    }
  }

  public handleError(error) {
    if (isSentryEnabled(this.config)) {
      return this.sentry.handleError(error);
    } else {
      debugError(error);
    }
  }
}

class LocalErrorHandler implements ErrorHandler {
  handleError(error) {
    debugError(error);
  }
}

export function provideErrorHandler(injector: Injector): ErrorHandler {
  if (isSentryEnabled(injector.get(AppConfigService))) {
    return injector.get(SentryErrorHandler);
  } else {
    return new LocalErrorHandler();
  }
}
